import React, { useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Anchor, xcss, Text, Stack } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { reportCard_reportsOverviewPage$key } from '@atlassian/jira-relay/src/__generated__/reportCard_reportsOverviewPage.graphql';
import { ReportImage } from '../report-image/index.tsx';

type ReportCardProps = {
	report: reportCard_reportsOverviewPage$key;
};

export const ReportCard = ({ report }: ReportCardProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const reportCardData = useFragment<reportCard_reportsOverviewPage$key>(
		graphql`
			fragment reportCard_reportsOverviewPage on JiraReport {
				...reportImage_reportsOverviewPage
				name @required(action: THROW)
				description @required(action: THROW)
				url @required(action: THROW)
			}
		`,
		report,
	);

	const name = reportCardData.name;
	const description = reportCardData.description;
	const url = reportCardData.url;

	const fireReportClickedAnalytics = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'reportButton', {
			report: name,
		});
	}, [createAnalyticsEvent, name]);

	const handleClick = useCallback(() => {
		fireReportClickedAnalytics();
	}, [fireReportClickedAnalytics]);

	return (
		<Anchor xcss={cardStyles} href={url} onClick={handleClick} aria-label={name}>
			<Stack space="space.100">
				<ReportImage report={reportCardData} />
				<Heading size="xsmall" as="h3">
					{name}
				</Heading>
				<Text size="medium" as="p">
					{description}
				</Text>
			</Stack>
		</Anchor>
	);
};

const cardStyles = xcss({
	height: '320px',
	width: '270px',
	display: 'flex',
	flexDirection: 'column',
	overflowX: 'hidden',
	overflowY: 'hidden',
	paddingLeft: 'space.150',
	paddingRight: 'space.150',
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
	border: '2px solid',
	borderColor: 'color.border',
	borderRadius: 'border.radius.200',
	backgroundColor: 'elevation.surface.overlay',
	':hover': {
		borderColor: 'color.border.focused',
		borderStyle: 'solid',
		borderWidth: 'space.025',
		overflowY: 'auto',
		textDecoration: 'none',
	},
	':focus-within': {
		borderColor: 'color.border.focused',
		borderStyle: 'solid',
		borderWidth: 'space.025',
		overflowY: 'auto',
		outline: 'none',
		textDecoration: 'none',
	},
	textDecoration: 'none',
});
